import { useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { analysisParameterError } from '../api/analysisParameters';
import type { AnalysisRebuildStatus } from '../types';
import { useAnalysisRebuildStatus } from '../hooks/useAnalysisRebuildStatus';
import type { ClipAnalysisMethods } from './ClipAnalysisMethodsDialog';
import AnalysisRebuildStatusLine from './AnalysisRebuildStatusLine';

export interface ClipMethodRebuild {
  analysis_key: string;
  display_name: string;
  rebuild: AnalysisRebuildStatus;
}

type CompletionCallback = (analysisKey: string, status: AnalysisRebuildStatus) => Promise<AnalysisRebuildStatus | undefined>;

function MethodRebuild({ deviceId, method, onCompleted }: { deviceId: number; method: ClipMethodRebuild; onCompleted?: CompletionCallback }) {
  const { status, pollExpired, error, reset } = useAnalysisRebuildStatus(deviceId, method.analysis_key,
    onCompleted ? status => onCompleted(method.analysis_key, status) : undefined);
  useEffect(() => { reset(method.rebuild); }, [method.rebuild, reset]);
  return <div>
    <AnalysisRebuildStatusLine name={method.display_name} status={status} pollExpired={pollExpired} />
    {error && <p role="alert">{method.display_name}: {error}</p>}
  </div>;
}

export default function ClipAnalysisRebuilds({ deviceId, methods, onCompleted }: { deviceId: number; methods?: ClipMethodRebuild[]; onCompleted?: CompletionCallback }) {
  const [loaded, setLoaded] = useState<ClipMethodRebuild[]>([]);
  const [error, setError] = useState('');
  useEffect(() => {
    if (methods) return;
    let active = true;
    axiosInstance.get<ClipAnalysisMethods>(`${config.devicesApiUrl}/analysis-methods/devices/${deviceId}`)
      .then(({ data }) => {
        if (active) setLoaded(data.methods.filter(method => method.enabled && method.rebuild.requested_at !== null));
      }).catch((failure: unknown) => {
        if (active) setError(analysisParameterError(failure));
      });
    return () => { active = false; };
  }, [deviceId, methods]);
  return <>
    {error && !methods && <p role="alert">Could not load rebuild status: {error}</p>}
    {(methods ?? loaded).map(method => <MethodRebuild key={`${deviceId}:${method.analysis_key}`} deviceId={deviceId} method={method} onCompleted={onCompleted} />)}
  </>;
}
